import { packFrameGrid } from "@/lib/frame-grid-packing";
import type { FrameData } from "@/lib/frame-data";
/** Shared chronological page layout for workspace frames. */
import type { Node } from "@xyflow/react";

import { resolveCreatedSize, WIDGET_FRAME_TYPES } from "@/lib/frame-defaults";
import {
  frameNodeSize,
  numericFrameDimension,
} from "@/lib/frame-node-geometry";
import {
  buildCanvasTimePages,
  type CanvasTimePage,
} from "@/components/canvas/canvas-time-pages";
import { CANVAS_TIME_PAGE_FULL_WIDTH_COLUMNS } from "@/lib/canvas-time-page-grid";

const TIDY_TIME_PAGE_COLS = CANVAS_TIME_PAGE_FULL_WIDTH_COLUMNS;

export const TIDY_TIME_PAGE_GAP_X = 640;
export const TIDY_TIME_PAGE_GAP_Y = 400;
export const TIDY_TIME_PAGE_COLUMNS = 3;

export interface TidyUpdate {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

type TidySize = Pick<TidyUpdate, "w" | "h">;

/**
 * Single-card widgets sized from their content at creation (event 400x132,
 * flight/music/weather at their creation size). Tidy may POSITION these
 * frames but must never STRETCH them: their update carries their current (or,
 * when the node has no explicit dimensions, creation) size, so any
 * width/height write-back downstream is a no-op. List frames with row-driven
 * heights (agenda/board/chat/slack/todo/calendar) keep standard tidy behavior.
 */
export const TIDY_WIDGET_FRAME_TYPES = WIDGET_FRAME_TYPES;

export function isTidyWidgetFrame(
  frameType: string | undefined | null,
): boolean {
  return (
    typeof frameType === "string" && TIDY_WIDGET_FRAME_TYPES.has(frameType)
  );
}

function tidyFrameType(node: Node<FrameData>): string | undefined {
  const frameType = node.data?.frameType;
  if (typeof frameType === "string" && frameType.length > 0) return frameType;
  return typeof node.type === "string" ? node.type : undefined;
}

function explicitFrameDimension(
  node: Node<FrameData>,
  axis: "width" | "height",
): number | undefined {
  if (axis === "width") {
    return (
      numericFrameDimension(node.width) ??
      numericFrameDimension(node.style?.width) ??
      numericFrameDimension(node.measured?.width)
    );
  }
  return (
    numericFrameDimension(node.height) ??
    numericFrameDimension(node.style?.height) ??
    numericFrameDimension(node.measured?.height)
  );
}

/** Widget size: keep the node size, falling back to the creation size. */
function widgetFrameSize(node: Node<FrameData>, frameType: string): TidySize {
  const created = resolveCreatedSize(frameType, {
    content: node.data?.content,
  });
  return {
    w: explicitFrameDimension(node, "width") ?? created.w,
    h: explicitFrameDimension(node, "height") ?? created.h,
  };
}

function currentFrameSize(node: Node<FrameData>): TidySize {
  const frameType = tidyFrameType(node);
  if (frameType && isTidyWidgetFrame(frameType)) {
    return widgetFrameSize(node, frameType);
  }
  const { width, height } = frameNodeSize(node);
  return { w: width, h: height };
}

/** Pack one time page from a local origin using the frames' current sizes. */
function packTimePage(frames: Node<FrameData>[]): TidyUpdate[] {
  return packFrameGrid(
    frames.map((node) => ({ id: node.id, ...currentFrameSize(node) })),
    TIDY_TIME_PAGE_COLS,
  );
}

export function tidyFramesByTimePage(
  nodes: Node<FrameData>[],
  options: { pages?: readonly CanvasTimePage[] } = {},
): TidyUpdate[] {
  const visible = nodes.filter((node) => !node.hidden);
  const pages = options.pages ?? buildCanvasTimePages(visible);
  if (pages.length === 0) return [];

  const nodeById = new Map(visible.map((node) => [node.id, node]));
  const pageLayouts = pages.map((page) => {
    const pageNodes = page.nodeIds.flatMap((id) => {
      const node = nodeById.get(id);
      return node ? [node] : [];
    });
    const frames = packTimePage(pageNodes);
    return {
      frames,
      width: Math.max(0, ...frames.map((frame) => frame.x + frame.w)),
      height: Math.max(0, ...frames.map((frame) => frame.y + frame.h)),
    };
  });

  const columnWidths = Array.from(
    { length: Math.min(TIDY_TIME_PAGE_COLUMNS, pageLayouts.length) },
    () => 0,
  );
  const rowHeights = Array.from(
    { length: Math.ceil(pageLayouts.length / TIDY_TIME_PAGE_COLUMNS) },
    () => 0,
  );
  pageLayouts.forEach((page, index) => {
    const column = index % TIDY_TIME_PAGE_COLUMNS;
    const row = Math.floor(index / TIDY_TIME_PAGE_COLUMNS);
    columnWidths[column] = Math.max(columnWidths[column]!, page.width);
    rowHeights[row] = Math.max(rowHeights[row]!, page.height);
  });
  const columnOrigins = columnWidths.map((_, index) =>
    columnWidths
      .slice(0, index)
      .reduce((sum, width) => sum + width + TIDY_TIME_PAGE_GAP_X, 0),
  );
  const rowOrigins = rowHeights.map((_, index) =>
    rowHeights
      .slice(0, index)
      .reduce((sum, height) => sum + height + TIDY_TIME_PAGE_GAP_Y, 0),
  );
  return pageLayouts.flatMap((page, index) => {
    const x = columnOrigins[index % TIDY_TIME_PAGE_COLUMNS]!;
    const y = rowOrigins[Math.floor(index / TIDY_TIME_PAGE_COLUMNS)]!;
    return page.frames.map((frame) => ({
      ...frame,
      x: frame.x + x,
      y: frame.y + y,
    }));
  });
}
